/**
 * Public API — tenant registration + approval polling.
 * POST requires Django CSRF: GET /api/v1/auth/csrf/ then POST with credentials + X-CSRFToken
 * (same pattern as authClient login). GET status uses credentials: "omit" + token query/bearer.
 *
 * @typedef {Object} TenantRegistrationRequest
 * @property {string} company_name
 * @property {string} owner_email
 * @property {string} owner_password
 * @property {string} owner_password_confirm
 * @property {string} [company_full_name]
 * @property {string} [company_address]
 * @property {string} [contact_phone]
 * @property {string} [license_no]
 * @property {string} [operation_no]
 * @property {string} [notes]
 */

/**
 * @typedef {Object} TenantRegistrationCreated
 * @property {string} id
 * @property {string} status_token
 * @property {string} [derived_schema_preview]
 */

/**
 * @typedef {Object} TenantRegistrationStatusResponse
 * @property {string} [status] - e.g. pending | approved | rejected
 * @property {string} [derived_schema_preview]
 */

import { PUBLIC_API_URL } from '@/config/api';
import { getCsrfToken, normalizeOrigin } from '@/services/authClient';

/** @typedef {Record<string, string>} FieldErrorMap */

export class TenantRegistrationApiError extends Error {
  /**
   * @param {string} message
   * @param {{ status: number, fieldErrors?: FieldErrorMap, body?: unknown }} opts
   */
  constructor(message, opts) {
    super(message);
    this.name = 'TenantRegistrationApiError';
    this.status = opts.status;
    this.fieldErrors = opts.fieldErrors || {};
    this.body = opts.body;
  }
}

/**
 * Normalize Django/DRF validation payload to single string per field.
 * @param {unknown} body
 * @returns {FieldErrorMap}
 */
export function parseFieldErrorsFromBody(body) {
  if (!body || typeof body !== 'object') return {};
  /** @type {FieldErrorMap} */
  const out = {};
  for (const [key, val] of Object.entries(body)) {
    if (key === 'detail' || key === 'non_field_errors') continue;
    if (Array.isArray(val) && val.length) {
      const first = val[0];
      out[key] = typeof first === 'string' ? first : String(first);
    } else if (typeof val === 'string') {
      out[key] = val;
    }
  }
  return out;
}

function registrationBaseUrl() {
  return `${normalizeOrigin(PUBLIC_API_URL)}/api/v1/tenant-registration`;
}

/**
 * How to send the status secret when polling GET …/tenant-registration/<id>/.
 * Set `REACT_APP_TENANT_REGISTRATION_STATUS_TOKEN_MODE` to match your backend:
 * - `status_token` (default): `?status_token=…`
 * - `token`: `?token=…`
 * - `bearer`: `Authorization: Bearer …` (no query param)
 */
export function tenantRegistrationStatusTokenMode() {
  const v = String(process.env.REACT_APP_TENANT_REGISTRATION_STATUS_TOKEN_MODE || 'status_token')
    .trim()
    .toLowerCase();
  if (v === 'token' || v === 'bearer') return v;
  return 'status_token';
}

/**
 * POST /api/v1/tenant-registration/ on the public API host.
 * @param {TenantRegistrationRequest} payload
 * @returns {Promise<TenantRegistrationCreated>}
 */
export async function submitTenantRegistration(payload) {
  const origin = normalizeOrigin(PUBLIC_API_URL);
  const csrfToken = await getCsrfToken(origin);
  const url = `${registrationBaseUrl()}/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    },
    body: JSON.stringify(payload),
  });

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : null;

  if (response.status === 429) {
    throw new TenantRegistrationApiError(
      'Too many requests. Please try again later.',
      { status: 429, body: data },
    );
  }

  if (!response.ok) {
    const fieldErrors = response.status === 400 ? parseFieldErrorsFromBody(data) : {};
    const message =
      (typeof data?.detail === 'string' && data.detail) ||
      (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
      'Registration failed';
    throw new TenantRegistrationApiError(message, {
      status: response.status,
      fieldErrors,
      body: data,
    });
  }

  return {
    id: String(data?.id ?? ''),
    status_token: String(data?.status_token ?? ''),
    derived_schema_preview: data?.derived_schema_preview != null
      ? String(data.derived_schema_preview)
      : undefined,
  };
}

/**
 * GET /api/v1/tenant-registration/<id>/
 * Sends the token per {@link tenantRegistrationStatusTokenMode}:
 * `?status_token=`, `?token=`, or `Authorization: Bearer`.
 * @param {string} id
 * @param {string} statusToken
 * @returns {Promise<TenantRegistrationStatusResponse>}
 */
export async function fetchTenantRegistrationStatus(id, statusToken) {
  const path = `${registrationBaseUrl()}/${encodeURIComponent(id)}/`;
  // Same-origin dev (empty PUBLIC_API_URL) uses a path-only base — new URL(relative) needs a base.
  const url = path.startsWith('http')
    ? new URL(path)
    : new URL(path, window.location.origin);
  const mode = tenantRegistrationStatusTokenMode();
  /** @type {Record<string, string>} */
  const headers = {};
  if (mode === 'bearer') {
    headers.Authorization = `Bearer ${statusToken}`;
  } else if (mode === 'token') {
    url.searchParams.set('token', statusToken);
  } else {
    url.searchParams.set('status_token', statusToken);
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'omit',
    headers: Object.keys(headers).length ? headers : undefined,
  });

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : null;

  if (response.status === 429) {
    throw new TenantRegistrationApiError(
      'Too many requests. Please try again later.',
      { status: 429, body: data },
    );
  }

  if (!response.ok) {
    throw new TenantRegistrationApiError(
      typeof data?.detail === 'string' ? data.detail : 'Could not load registration status',
      { status: response.status, body: data },
    );
  }

  return /** @type {TenantRegistrationStatusResponse} */ (data && typeof data === 'object' ? data : {});
}

/**
 * @param {string} [status]
 * @returns {boolean}
 */
export function isTerminalRegistrationStatus(status) {
  const s = String(status || '').toLowerCase();
  return s === 'approved' || s === 'rejected';
}
